import { makeAutoObservable, runInAction } from "mobx";
import { isAdmin } from "auth/tier";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded } from "stores/sessionGuard";

/** Minimal shape of a `find` response when the service wraps rows in `{ data }`. */
interface FindEnvelope<T> {
  data?: T[];
  total?: number;
}

export type IntegrationErrorStatus = "new" | "acknowledged" | "resolved";

/**
 * A stored integration/ingestion failure. `payload` is the ORIGINAL inbound
 * request body kept verbatim as a string (usually JSON); `detail` is the
 * stringified validation errors / stack. Both may be large. Rows are written
 * server-side only — there is no external `create`.
 */
export interface IntegrationError {
  id: string;
  source: string;
  errorType: string | null;
  message: string | null;
  detail: string | null;
  payload: string | null;
  method: string | null;
  path: string | null;
  statusCode: number | null;
  status: IntegrationErrorStatus;
  acknowledgedAt: string | null;
  acknowledgedBy: number | null;
  createdAt: string;
}

export type IntegrationErrorStatusFilter = "all" | IntegrationErrorStatus;

export type IntegrationErrorSortKey = "createdAt" | "source" | "errorType" | "status";

/**
 * The server caps `integration-errors` find at 500 rows
 * (`Math.min(Number(query.$limit) || 100, 500)`) and returns a BARE ARRAY with
 * no `total` and no `$skip` support. 500 is therefore both the most we can ask
 * for and the point past which the true number is unobtainable from this API —
 * see `IntegrationErrorsStore.truncated`.
 */
export const INTEGRATION_ERRORS_SERVER_LIMIT = 500;

/**
 * The CSV projection of one integration error: a TRIAGE row.
 *
 * `payload` and `detail` are deliberately excluded. `payload` is the original
 * inbound request body kept verbatim — for webhook sources that routinely means
 * third-party tokens, signatures and meeting content, and `detail` is a raw
 * stack/validation dump. Both are unbounded in size and would turn a triage
 * spreadsheet into an exfiltration of the very payloads that failed. They stay
 * in the admin-only detail dialog, where they are read one row at a time under
 * the API's admin gate rather than copied out in bulk.
 */
export interface IntegrationErrorExportRow extends Record<string, unknown> {
  createdAt: string;
  source: string;
  errorType: string;
  message: string;
  method: string;
  path: string;
  statusCode: number | null;
  status: string;
  acknowledgedAt: string;
  id: string;
}

/** Shape one error row into its export row. Pure — no store, no formatting. */
export function toIntegrationErrorExportRow(
  row: IntegrationError
): IntegrationErrorExportRow {
  return {
    createdAt: row.createdAt ?? "",
    source: row.source ?? "",
    errorType: row.errorType ?? "",
    message: row.message ?? "",
    method: row.method ?? "",
    path: row.path ?? "",
    statusCode: row.statusCode ?? null,
    status: row.status,
    acknowledgedAt: row.acknowledgedAt ?? "",
    id: row.id,
  };
}

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<IntegrationErrorSortKey> = new Set(["createdAt"]);

/**
 * View store for the /admin/integration-errors viewer.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The `integration-errors` service is read through the
 * registered ServiceStore (see serviceStores/admin.stores.ts). Its `find`
 * resolves a BARE ARRAY (newest first, not paginated), so `list` is the working
 * snapshot of that response — filtering (status / source / search) is applied
 * client-side so the summary counts stay accurate across the whole set. The
 * store never accumulates entities of its own beyond that snapshot.
 */
class IntegrationErrorsStore {
  rootStore: RootStore;

  list: IntegrationError[] = [];
  loading = false;
  /** A failed acknowledge / resolve / reopen / delete. */
  error: string | null = null;
  /** The list did not load — the table says so instead of "No integration
   *  errors match", and the counts are not shown as zeros. */
  loadError: string | null = null;

  // Client-side filters (counts stay accurate across the full loaded set).
  statusFilter: IntegrationErrorStatusFilter = "all";
  sourceFilter = "all";
  search = "";

  // Column sort (applied client-side over the filtered set, like the filters)
  sortKey: IntegrationErrorSortKey = "createdAt";
  sortDir: SortDir = "desc";

  // Detail dialog selection.
  selectedId: string | null = null;

  // Bounds the server fetch; the service returns an array, not a page. This is
  // the server's own hard cap — asking for more is silently clamped.
  limit = INTEGRATION_ERRORS_SERVER_LIMIT;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `integration-errors` ServiceStore (see admin.stores.ts). */
  private get service(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("integration-errors") ??
        this.rootStore.integrationErrorsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** Mutating actions are admin-only in the UI (the API re-enforces this). */
  get canManage(): boolean {
    return isAdmin(this.rootStore);
  }

  // ---- Derived data -------------------------------------------------------

  /** Distinct sources present in the loaded rows, sorted, for the source select. */
  get sourceOptions(): string[] {
    const seen = new Set<string>();
    for (const row of this.list) if (row.source) seen.add(row.source);
    return Array.from(seen).sort();
  }

  /** Rows after status / source / search filters (newest-first order preserved). */
  get filteredList(): IntegrationError[] {
    const needle = this.search.trim().toLowerCase();
    return this.list.filter((row) => {
      if (this.statusFilter !== "all" && row.status !== this.statusFilter) return false;
      if (this.sourceFilter !== "all" && row.source !== this.sourceFilter) return false;
      if (needle) {
        const haystack = [row.source, row.errorType, row.message, row.path, row.method]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (e.g. a missing errorType) sort last in either direction.
   */
  get sortedList(): IntegrationError[] {
    const key = this.sortKey;
    const valueOf = (row: IntegrationError): SortValue => {
      switch (key) {
        case "createdAt":
          return dateValue(row.createdAt);
        case "source":
          return textValue(row.source);
        case "errorType":
          return textValue(row.errorType);
        case "status":
          return textValue(row.status);
      }
    };
    return sortRows(this.filteredList, valueOf, this.sortDir);
  }

  /**
   * True when the loaded snapshot may not be every stored error. The service
   * caps at 500, returns a bare array with no `total`, and supports no `$skip`
   * — so a full response back is indistinguishable from "exactly 500 exist",
   * and the true figure is simply not obtainable from this API. Every count
   * below is therefore a LOWER BOUND once this is true, and the cards say so
   * rather than printing 500 as if it were the answer.
   */
  get truncated(): boolean {
    return this.list.length >= this.limit;
  }

  /** Suffix turning a count into a lower bound when the snapshot is capped. */
  get countSuffix(): string {
    return this.truncated ? "+" : "";
  }

  get total(): number {
    return this.list.length;
  }

  get newCount(): number {
    return this.list.filter((r) => r.status === "new").length;
  }

  get acknowledgedCount(): number {
    return this.list.filter((r) => r.status === "acknowledged").length;
  }

  get resolvedCount(): number {
    return this.list.filter((r) => r.status === "resolved").length;
  }

  /**
   * The filtered + sorted rows flattened to plain rows for CSV export, so the
   * file carries exactly the triage list on screen (filters and order
   * included). Payload/detail are excluded by the row shaper — see
   * `toIntegrationErrorExportRow`.
   */
  get exportRows(): IntegrationErrorExportRow[] {
    return this.sortedList.map(toIntegrationErrorExportRow);
  }

  /** Filename for the CSV, stamped as partial when the server cap was hit. */
  get exportFileName(): string {
    const stamp = new Date().toISOString().slice(0, 10);
    return this.truncated
      ? `integration-errors_newest-${this.limit}_${stamp}`
      : `integration-errors_${stamp}`;
  }

  /** The row backing the open detail dialog, resolved live from the list. */
  get selected(): IntegrationError | null {
    if (this.selectedId === null) return null;
    return this.list.find((r) => r.id === this.selectedId) ?? null;
  }

  /**
   * Pretty-print a stored JSON string with 2-space indent; fall back to the raw
   * string when it does not parse (payloads/details are not guaranteed JSON).
   */
  prettyJson(value: string | null): string {
    if (!value) return "";
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  // ---- Filter / selection setters ----------------------------------------

  setStatusFilter(value: IntegrationErrorStatusFilter) {
    this.statusFilter = value;
  }

  setSourceFilter(value: string) {
    this.sourceFilter = value;
  }

  setSearch(value: string) {
    this.search = value;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (When newest-first, text A→Z). */
  setSort(key: IntegrationErrorSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  select(id: string | null) {
    this.selectedId = id;
  }

  clearSelection() {
    this.selectedId = null;
  }

  // ---- Loading ------------------------------------------------------------

  get query(): Record<string, unknown> {
    return { $limit: this.limit };
  }

  async load() {
    this.loading = true;
    this.loadError = null;
    try {
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // rendered as "No integration errors" and four green zeros.
      const result = await findOrThrow<
        IntegrationError[] | FindEnvelope<IntegrationError> | null
      >(this.service, { query: this.query });
      const rows = Array.isArray(result) ? result : (result?.data ?? []);
      runInAction(() => {
        this.list = rows;
      });
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.list = [];
        this.loadError = loadErrorMessage(err, "Failed to load integration errors");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  // ---- Mutating actions (admin-gated) ------------------------------------

  private async patchAction(id: string, action: "acknowledge" | "resolve" | "reopen") {
    if (!this.canManage) return;
    this.error = null;
    try {
      await this.service?.patch(id, { action } as never);
      await this.load();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : `Failed to ${action}`;
      });
    }
  }

  async acknowledge(id: string) {
    await this.patchAction(id, "acknowledge");
  }

  async resolve(id: string) {
    await this.patchAction(id, "resolve");
  }

  async reopen(id: string) {
    await this.patchAction(id, "reopen");
  }

  async remove(id: string) {
    if (!this.canManage) return;
    this.error = null;
    try {
      await this.service?.remove(id);
      runInAction(() => {
        if (this.selectedId === id) this.selectedId = null;
      });
      await this.load();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Failed to delete";
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.list = [];
    this.loading = false;
    this.error = null;
    this.loadError = null;
    this.statusFilter = "all";
    this.sourceFilter = "all";
    this.search = "";
    this.sortKey = "createdAt";
    this.sortDir = "desc";
    this.selectedId = null;
    this.limit = INTEGRATION_ERRORS_SERVER_LIMIT;
  }
}

export default IntegrationErrorsStore;
